import React, { createElement, FC, PropsWithChildren } from "react";
import hypernova from "hypernova";
import camelcaseKeys from "camelcase-keys";
import {
  createHypernovaContext,
  HydrateAtRoot,
  hydrateIntoContainer,
  RenderAtRoot,
} from "@wss/react";

type HypernovaProps = ReturnType<(typeof hypernova)["load"]>[number];

function getHypernovaData(name: string, camelCase = false): HypernovaProps {
  const result = hypernova.load(name)[0];
  const props = camelCase
    ? camelcaseKeys(result.data, { deep: true })
    : result.data;

  return { node: result.node, data: props };
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export function withPreloadedJSON<T extends unknown>(
  Component: FC<PropsWithChildren<T>>,
  name: string,
  camelCase = false,
): FC {
  const { node, data } = getHypernovaData(name, camelCase);
  const Context = createHypernovaContext<T>(name, data);
  return function RenderElement() {
    if (!node.firstElementChild) {
      return (
        <RenderAtRoot container={node}>
          <Context.Provider value={data}>
            {createElement(Component, data)}
          </Context.Provider>
        </RenderAtRoot>
      );
    }
    return (
      <HydrateAtRoot container={node}>
        <Context.Provider value={data}>
          {createElement(Component, data)}
        </Context.Provider>
      </HydrateAtRoot>
    );
  };
}

/**
 * Used when you want to mount a singular component without a shared root.
 *
 * This maybe used when just a portion of a page is written in React and needs interactivity.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export function hydrateHypernovaComponent<T extends unknown>(
  Component: FC<PropsWithChildren<T>>,
  name: string,
  camelCase = false,
) {
  const { node, data } = getHypernovaData(name, camelCase);
  const Context = createHypernovaContext<T>(data);
  hydrateIntoContainer(
    node,
    <Context.Provider value={data}>
      {createElement(Component, data)}
    </Context.Provider>,
  );
}
